<template>
  <highcharts
    class="delta-chart"
    :options="deltaChartOptions"
  />
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';

import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import convertToCelsius from '@shared/lib/converter/convertToCelsius';
import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';

export default {
  name: 'DeltaT',
  mixins: [unitPreferencesMixin],
  props: {
    graphData: Array,
  },
  computed: {
    ...mapState({
      csvGenerated: state => state.customerDetail.csvGenerated,
    }),
    deltaGraphData() {
      const deltaTemp = [];
      const deltaAvgTemp = [];
      const oaTemp = [];
      const cycleDate = [];
      let oaTempMin = null;
      let oaTempMax = null;

      this.graphData.forEach((c) => {
        const {
          supplyAir,
          returnAir,
          atticAir,
          acMode,
          deltaT,
          dailyStat,
          startTime,
          tempAPI,
        } = c;

        if (tempAPI !== null && tempAPI !== undefined) {
          let temp = tempAPI;
          if (this.tempFormat === '°C') {
            temp = convertToCelsius(temp);
          }
          oaTemp.unshift(temp);
          if (!oaTempMin) {
            oaTempMin = temp;
          } else if (oaTempMin > temp) {
            oaTempMin = temp;
          }
          if (!oaTempMax) {
            oaTempMax = temp;
          } else if (oaTempMax < temp) {
            oaTempMax = temp;
          }
        }

        let dT = deltaT;
        if (this.tempFormat === '°C') {
          dT = convertToCelsiusResult(dT)
        }
        deltaTemp.unshift(Math.abs(dT));

        if (dailyStat) {
          let temp = null;
          if (acMode === 'Heat') {
            temp = dailyStat.heat;
          }
          if (acMode === 'Cool') {
            temp = dailyStat.cool;
          }
          if (this.tempFormat === '°C') {
            temp = convertToCelsiusResult(temp);
          }
          deltaAvgTemp.unshift(Math.abs(temp));
        } else {
          deltaAvgTemp.unshift(null);
        }

        if (startTime) {
          let date = moment(startTime).format('M/D/YY h:mm:ss a');
          if (this.dateFormat === 'D/M') {
            date = moment(startTime).format('D/M/YY h:mm:ss a');
          }
          cycleDate.unshift(date);
        }
      });

      return {
        deltaTemp,
        deltaAvgTemp,
        oaTemp,
        cycleDate,
        oaTempMax,
        oaTempMin,
      };
    },
    deltaChartOptions() {
      const {
        selectPoint,
        // Don't remove this value it is used to refresh the chart
        // Find a better way to refresh later
        csvGenerated,
      } = this;
      const {
        deltaTemp,
        deltaAvgTemp,
        oaTemp,
        cycleDate,
        oaTempMax,
        oaTempMin,
      } = this.deltaGraphData;

      const {
        tempFormat,
        dateFormat,
      } = this;

      const selectedIndex = [];
      return {
        title: '',
        chart: {
          type: 'line',
          zoomType: 'x',
          height: 500,
          spacingTop: 50,
        },
        credits: {
          enabled: false,
        },
        yAxis: [{
          labels: {
            format: `{value}${tempFormat}`,
          },
          title: {
            text: 'ΔT Temperature',
          },
          min: Math.min(...deltaTemp) - 4,
          max: Math.max(...deltaTemp) + 4,
        }, {
          labels: {
            format: `{value}${tempFormat}`,
          },
          opposite: true,
          title: {
            text: 'OA Temperature',
          },
          min: oaTempMin - 4,
          max: oaTempMax + 4,
        }],
        xAxis: [{
          categories: cycleDate,
        }],
        legend: {
          layout: 'vertical',
          align: 'right',
          verticalAlign: 'middle',
        },
        plotOptions: {
          series: {
            label: {
              connectorAllowed: true,
            },
            connectNulls: true,
            marker: {
              states: {
                select: {
                  lineColor: 'rgba(100,100,200, 0.25)',
                  fillColor: 'rgba(223, 83, 83, 1)',
                  lineWidth: 10,
                  radius: 6,
                },
              },
            },
            point: {
              events: {
                click() {
                  let unMarkIndex;
                  const idx = selectedIndex.indexOf(this.index);
                  if (idx !== -1) {
                    selectedIndex.splice(idx, 1);
                  } else {
                    if (selectedIndex.length >= 2) {
                      unMarkIndex = selectedIndex.shift();
                    }
                    selectedIndex.push(this.index);
                  }
                  this.series.chart.series.forEach((s) => {
                    if (unMarkIndex) {
                      // eslint-disable-next-line
                      s.data[unMarkIndex].selected = false;
                    }
                    // eslint-disable-next-line
                    s.data[this.index].selected = !s.data[this.index].selected;
                    s.redraw();
                  });
                  selectPoint(this);
                },
              },
            },
          },
        },
        series: [{
          name: 'Delta T',
          color: 'rgba(240, 120, 0, 1)',
          data: deltaTemp,
        }, {
          name: 'Delta T Average',
          color: 'rgba(0, 255, 0, 1)',
          data: deltaAvgTemp,
        }, {
          name: 'Outside T',
          color: 'rgba(119, 152, 191, 1)',
          data: oaTemp,
          yAxis: 1,
        }],
        responsive: {
          rules: [{
            condition: {
              maxWidth: 2000,
            },
            chartOptions: {
              legend: {
                layout: 'horizontal',
                align: 'center',
                verticalAlign: 'bottom',
              },
            },
          }],
        },
      };
    },
  },
  methods: {
    selectPoint(point) {
      const data = this.graphData[this.graphData.length - point.index];
      this.$store.dispatch('selectDataForExport', {
        data,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.delta-chart {
  margin-bottom: 16px;
  width: 100%;
  max-width: 1705px;
}
</style>
